import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Plus, User, LogOut, MapPin, Shield, MessageSquare } from "lucide-react";

const NAV = [
  { to: "/needs", label: "Needs" },
  { to: "/marketplace", label: "Marketplace" },
  { to: "/events", label: "Events" },
  { to: "/jobs", label: "Jobs" },
  { to: "/businesses", label: "Businesses" },
  { to: "/leaderboard", label: "Helpers" },
];

function Brand() {
  return (
    <Link to="/" data-testid="nav-brand" className="group inline-flex items-center">
      <span className="font-display text-3xl sm:text-4xl tracking-tight text-[#1E3A2F] group-hover:text-[#C05A3D] transition-colors">
        HOME
      </span>
    </Link>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const load = () => api.get("/conversations/unread_count").then((r) => alive && setUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F4EB]">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Brand />
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors abm-link-underline ${
                    isActive ? "text-[#C05A3D]" : "text-[#1E3A2F] hover:text-[#C05A3D]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user && (
              <button
                type="button"
                onClick={() => nav("/inbox")}
                data-testid="header-inbox-btn"
                aria-label="Inbox"
                className="relative w-10 h-10 rounded-full grid place-items-center text-[#1E3A2F] hover:bg-[#1E3A2F]/5 transition"
              >
                <MessageSquare className="w-5 h-5" />
                {unread > 0 && (
                  <span data-testid="inbox-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C05A3D] text-white text-[10px] font-bold grid place-items-center">
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>
            )}
            <Button
              data-testid="header-post-btn"
              onClick={() => nav(user ? "/post/listing" : "/login")}
              className="hidden sm:inline-flex abm-btn-primary rounded-full h-10 px-4"
            >
              <Plus className="w-4 h-4 mr-1" /> Post
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="inline-flex items-center gap-2 h-10 pl-1 pr-3 rounded-full bg-[#1E3A2F] text-[#F7F4EB] font-semibold hover:bg-[#2E5A45] transition"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#F7F4EB] text-[#1E3A2F] text-sm flex items-center justify-center">
                      {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                    </div>
                    {user.role === "admin" && (
                      <span data-testid="admin-pill" className="hidden sm:inline text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#C05A3D] text-white">Admin</span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">
                    {user.name || user.email}
                    {user.role === "admin" && <div className="text-[10px] font-semibold uppercase tracking-widest text-[#C05A3D] mt-0.5">Administrator</div>}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => nav("/profile")}>
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-inbox" onClick={() => nav("/inbox")}>
                    <MessageSquare className="w-4 h-4 mr-2" /> Inbox
                    {unread > 0 && <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#C05A3D] text-white">{unread}</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-post-listing" onClick={() => nav("/post/listing")}>
                    <Plus className="w-4 h-4 mr-2" /> New Listing
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-post-need" onClick={() => nav("/post/need")}>
                    <Plus className="w-4 h-4 mr-2" /> New Need / Offer
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-post-event" onClick={() => nav("/post/event")}>
                    <Plus className="w-4 h-4 mr-2" /> New Event
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-post-job" onClick={() => nav("/post/job")}>
                    <Plus className="w-4 h-4 mr-2" /> New Job
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="menu-admin-moderation" onClick={() => nav("/admin/moderation")}>
                        <Shield className="w-4 h-4 mr-2" /> Moderation
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-logout"
                    onClick={async () => {
                      await logout();
                      nav("/");
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  data-testid="header-signin-btn"
                  variant="ghost"
                  onClick={() => nav("/login")}
                  className="hidden sm:inline-flex rounded-full h-10 px-4 text-[#1E3A2F]"
                >
                  Sign in
                </Button>
                <Button
                  data-testid="header-join-btn"
                  onClick={() => nav("/signup")}
                  className="abm-btn-pine rounded-full h-10 px-4"
                >
                  Join
                </Button>
              </>
            )}
            <Sheet>
              <SheetTrigger asChild className="md:hidden">
                <button data-testid="mobile-menu-trigger" aria-label="Menu" className="w-10 h-10 grid place-items-center">
                  <Menu className="w-5 h-5 text-[#1E3A2F]" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#F7F4EB]">
                <div className="mt-8 flex flex-col gap-4">
                  {NAV.map((n) => (
                    <Link key={n.to} to={n.to} className="text-lg font-medium text-[#1E3A2F]">
                      {n.label}
                    </Link>
                  ))}
                  <Link to="/post/listing" className="text-lg font-medium text-[#C05A3D]">Post a listing</Link>
                  <Link to="/post/event" className="text-lg font-medium text-[#C05A3D]">Post an event</Link>
                  <Link to="/post/job" className="text-lg font-medium text-[#C05A3D]">Post a job</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-24 bg-[#1E3A2F] text-[#F7F4EB]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="bg-[#F7F4EB]/95 rounded-2xl p-3 inline-block">
              <img src="/abm-logo.png" alt="African Bridge Media" className="h-14 w-auto object-contain" />
            </div>
            <p className="mt-4 text-sm text-[#A3B3AA] max-w-md leading-relaxed">
              A community marketplace and events hub for the African diaspora across
              Washington State — from Seattle to Spokane. Buy, share, gather, celebrate.
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-[#DDA74A]">
              <MapPin className="w-4 h-4" /> Washington State, USA
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#DDA74A] mb-3">Explore</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/needs" className="hover:text-[#DDA74A]">Needs & Offers</Link></li>
              <li><Link to="/businesses" className="hover:text-[#DDA74A]">Businesses</Link></li>
              <li><Link to="/leaderboard" className="hover:text-[#DDA74A]">Community Helpers</Link></li>
              <li><Link to="/marketplace" className="hover:text-[#DDA74A]">Marketplace</Link></li>
              <li><Link to="/events" className="hover:text-[#DDA74A]">Events</Link></li>
              <li><Link to="/jobs" className="hover:text-[#DDA74A]">Healthcare Jobs</Link></li>
              <li><Link to="/post/listing" className="hover:text-[#DDA74A]">Post a listing</Link></li>
              <li><Link to="/post/event" className="hover:text-[#DDA74A]">Post an event</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#DDA74A] mb-3">Community</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-[#DDA74A]">About</Link></li>
              <li><Link to="/contact" className="hover:text-[#DDA74A]">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-[#DDA74A]">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-[#DDA74A]">Terms</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 text-xs text-[#A3B3AA] flex flex-col sm:flex-row items-center justify-between gap-2">
            <div>© {new Date().getFullYear()} African Bridge Media. Built with love in the Pacific Northwest.</div>
            <div>Ubuntu · Community · Belonging</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
